import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import Footer from "../../../core/common/Footer";
import SearchableSelect from "../../../core/common/SearchableSelect";
import useProjects from "../../../core/customhook/useProjects";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";
import z from "zod";
import CryptoJS from "crypto-js";
import MultiSearchableSelect from "../../../core/common/MultiSearchableSelect";

const required = "This field is required.";

export const userSchema = z.object({
  full_name: z.string().trim().min(1, required),

  email: z
    .string()
    .trim()
    .min(1, required)
    .email("Please enter a valid email address."),

  phone: z
    .string()
    .trim()
    .min(1, required)
    .regex(/^[6-9]\d{9}$/, "Please enter a valid 10-digit mobile number."),

  password: z.string().trim().min(1, required).min(6, "Password must be at least 6 characters."),
});

export const mappingSchema = z.object({
  project_id: z.string().trim().min(1, "Please select a Brand."),
  program_id: z.string().trim().min(1, "Please select a Program."),
  department_id: z
    .array(z.string())
    .min(1, "Please select at least one department."),
});
 
const collectFirstErrorPerField = (issues: z.ZodIssue[]) => {
  const fieldErrors: Record<string, string> = {};
  issues.forEach((issue) => {
    const key = issue.path[0] as string;
    if (!fieldErrors[key]) {
      fieldErrors[key] = issue.message;
    }
  });
  return fieldErrors;
};

const AddUsers = () => {
  const { projects} = useProjects();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
  });

  const [mapping, setMapping] = useState({
    project_id: "",
    program_id: "",
    department_id: [] as string[],
  });

  const [mappings, setMappings] = useState<any[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [mappingErrors, setMappingErrors] = useState<Record<string, string>>(
    {},
  );

  const [programList, setProgramList] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  const getPrograms = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getDepartments = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const createUser = useApi<any>(`/api/v1/admin/auth/spoc`, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const togglePasswordVisibility = (field: "password") => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const validateForm = () => {
    const result = userSchema.safeParse(formData);

    if (!result.success) {
      setErrors(collectFirstErrorPerField(result.error.issues));
      return false;
    }

    setErrors({});
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target; 
    const nextValue = name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFieldDataChange = (
    name: string,
    value: string,
    label?: string
  ) => {
    if (name === "project_id") {
      handleProjectChange(value);
      return;
    }

    if (name === "program_id") {
      handleProgramChange(value);
      return;
    }

    setMapping((prev) => ({
      ...prev,
      [name]: value,
    }));

    setMappingErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleDepartmentChange = (
    name: string,
    values: string[],
    selectedOptions: any[]
  ) => {
    setMapping((prev) => ({
      ...prev,
      [name]: values,
    }));

    setMappingErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleProjectChange = async (projectId: string) => {
    setMapping({
      project_id: projectId,
      program_id: "",
      department_id: [],
    });

    setProgramList([]);
    setDepartmentList([]); 
    setMappingErrors((prev) => ({
      ...prev,
      project_id: "",
      program_id: "",
      department_id: "",
    }));

    try {
      const res = await getPrograms.callApi(
        {},
        `/api/v1/master/projects/${projectId}/programs`,
      );

      if (res?.success) {
        setProgramList(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load programs.",
      });
    }
  };

  const handleProgramChange = async (programId: string) => {
    setMapping((prev) => ({
      ...prev,
      program_id: programId,
      department_id: [],
    }));

    setDepartmentList([]); 
    setMappingErrors((prev) => ({
      ...prev,
      program_id: "",
      department_id: "",
    }));

    try {
      const res = await getDepartments.callApi(
        {},
        `/api/v1/master/programs/${programId}/departments`,
      );

      if (res?.success) {
        setDepartmentList(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load departments.",
      });
    }
  };

  const handleAddMapping = () => { 
    const isUserDetailsValid = validateForm();

    const mappingResult = mappingSchema.safeParse(mapping);
    const isMappingValid = mappingResult.success;

    setMappingErrors(
      isMappingValid
        ? {}
        : collectFirstErrorPerField(mappingResult.error.issues),
    );

    if (!isUserDetailsValid || !isMappingValid) {
      if (!isUserDetailsValid) {
        Swal.fire({
          icon: "warning",
          title: "User Details Required",
          text: "Please fill Full Name, Email, Phone and Password before adding a mapping.",
        });
      }

      return;
    }
 
    const alreadyExists = mappings.some((item) => {
      if (
        item.project_id !== mapping.project_id ||
        item.program_id !== mapping.program_id
      ) {
        return false;
      }

      const oldDepartments = [...item.department_id].sort().join(",");
      const newDepartments = [...mapping.department_id].sort().join(",");

      return oldDepartments === newDepartments;
    });
    if (alreadyExists) {
      Swal.fire({
        icon: "warning",
        title: "Already Added",
        text: "This mapping has already been added.",
      });

      return;
    }

    const selectedProject = projects.find(
      (item: any) => item.project_id === mapping.project_id,
    );

    const selectedProgram = programList.find(
      (item: any) => item.program_id === mapping.program_id,
    );

    const selectedDepartments = departmentList.filter((item: any) =>
      mapping.department_id.includes(item.department_id),
    );

    setMappings((prev) => [
      ...prev,
      {
        project_id: mapping.project_id,
        project_name: selectedProject?.brand_name,

        program_id: mapping.program_id,
        program_name: selectedProgram?.program_name,

        department_id: mapping.department_id,
        department_name: selectedDepartments
          .map((d: any) => d.department_name)
          .join(", "),
      },
    ]);

    setMapping({
      project_id: "",
      program_id: "",
      department_id: [],
    });

    setProgramList([]);
    setDepartmentList([]);
    setMappingErrors({});
  };

  const handleRemoveMapping = (index: number) => {
    Swal.fire({
      title: "Remove Mapping?",
      text: "This mapping will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setMappings((prev) => prev.filter((_, i) => i !== index));

      Swal.fire({
        icon: "success",
        title: "Removed",
        text: "Mapping removed successfully.",
        timer: 1200,
        showConfirmButton: true,
      });
    });
  }; 
  const handleSubmit = async () => { 
    if (!validateForm()) {
      return;
    }
  
    if (mappings.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Mapping Added",
        text: "Please add at least one Brand, Program and Department mapping.",
      });
 
      return;
    }
 
    const hashedPassword = CryptoJS.SHA256(formData?.password).toString();
 
    const payload = {
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      password: hashedPassword,
      normalPassword: formData?.password,
 
      mappings: mappings.flatMap((item) =>
        item.department_id.map((deptId: string) => ({
          project_id: item.project_id,
          program_id: item.program_id,
          department_id: deptId,
        })),
      ),
    };
 
    try {
      const res = await createUser.callApi(payload);
 
      if (!res?.success) { 
        throw new Error(res?.error || res?.message || "Unable to create user.");
      }
 
      const result = await Swal.fire({
        icon: "success",
        title: "User Created",
        text: "The user has been created successfully.",
        confirmButtonText: "OK",
      });
 
      if (result.isConfirmed) {
        navigate(all_routes.userSetup);
      }
    } catch (error: any) { 
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to create user."; 
      if (
        error?.response?.data?.code === "CONFLICT" ||
        /email/i.test(apiMessage)
      ) {
        setErrors((prev) => ({
          ...prev,
          email: apiMessage,
        }));
      }
 
      Swal.fire({
        icon: "error",
        title: "Creation Failed",
        text: apiMessage,
      });
    }
  };
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Add User</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">User Management</li>
                <li className="breadcrumb-item active">Add User</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">User Details</h5>
            <p>Enter the user's basic information and login credentials.</p>
          </div>

          <div className="card-body">
            {/* ===================== USER INFORMATION ===================== */}
            <div className="mb-4">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Full Name <span className="text-danger">*</span>
                  </label>

                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.full_name ? "is-invalid" : ""
                    }`}
                    placeholder="Enter Full Name"
                  />

                  {errors.full_name && (
                    <div className="invalid-feedback">{errors.full_name}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Email Address <span className="text-danger">*</span>
                  </label>

                  <input
                    type="email"
                    name="email"
                    autoComplete="off"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Enter Email Address"
                  />

                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Mobile Number <span className="text-danger">*</span>
                  </label> 
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.phone ? "is-invalid" : ""
                    }`}
                    placeholder="Enter Mobile Number"
                  />

                  {errors.phone && (
                    <div className="invalid-feedback">{errors.phone}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Password <span className="text-danger">*</span>
                  </label>

                  <div className="pass-group">
                    <input
                      type={passwordVisibility.password ? "text" : "password"}
                      className={`form-control pass-input ${
                        errors.password ? "is-invalid" : ""
                      }`}
                      autoComplete="false"
                      name="password"
                      value={formData.password}
                      placeholder="Enter Password"
                      onChange={handleChange}
                    />

                    <span
                      className={`ti toggle-passwords ${
                        passwordVisibility.password ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => togglePasswordVisibility("password")}
                    />

                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <hr
              className="my-4"
              style={{
                border: "none",
                borderTop: "1.5px solid #6c757d",
              }}
            /> 

            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h5 className="mb-0 fw-bold">User Mapping</h5>

                  <small className="text-muted">
                    Assign the user to a brand, program, and department to configure their access.
                  </small>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <SearchableSelect
                    label="Brand Name"
                    name="project_id"
                    value={mapping.project_id}
                    options={projects.map((item: any) => ({
                      label: item.project_name,
                      value: item.project_id,
                    }))}
                    onChange={handleFieldDataChange}
                    error={mappingErrors.project_id}
                    required
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <SearchableSelect
                    label="Program Name"
                    name="program_id"
                    value={mapping.program_id}
                    options={programList.map((item: any) => ({
                      label: item.program_name,
                      value: item.program_id,
                    }))}
                    onChange={handleFieldDataChange}
                    disabled={!mapping.project_id}
                    error={mappingErrors.program_id}
                    required
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <MultiSearchableSelect
                    label="Department"
                    name="department_id"
                    value={mapping.department_id}
                    options={departmentList.map((item: any) => ({
                      label: item.department_name,
                      value: item.department_id,
                    }))}
                    onChange={handleDepartmentChange}
                    disabled={!mapping.program_id}
                    error={mappingErrors.department_id}
                    required
                  />
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-end mb-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddMapping}
                >
                  <i className="ti ti-plus me-1"></i>
                  Add Mapping
                </button>
              </div>
            </div>

            <hr
              className="my-4"
              style={{
                border: "none",
                borderTop: "1.5px solid #6c757d",
              }}
            />

            {/* ===================== ASSIGNED MAPPING ===================== */}

            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0 fw-bold">Assigned Mappings</h5>

                  <small className="text-muted">
                    Review the brand, program, and department combinations assigned to this user.
                  </small>
                </div>
              </div>

              {mappings.length === 0 ? (
                <div className="border rounded-3 bg-light text-center py-4">
                  <i
                    className="ti ti-folder-plus text-secondary"
                    style={{ fontSize: 30 }}
                  ></i>

                  <h5 className="mt-2">No Mapping Added</h5>

                  <p className="text-muted mb-0">
                    Select a brand, program, and department, then click <strong> Add Mapping</strong>.
                  </p>
                </div>
              ) : (
                <div className="table-responsive border rounded-3">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 70 }}>S.No</th>
                        <th>Brand</th>
                        <th>Program</th>
                        <th>Department</th>
                        <th style={{ width: 80 }}>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {mappings.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>

                          <td className="fw-semibold">{item.project_name}</td>

                          <td>{item.program_name}</td>

                          <td>{item.department_name}</td>

                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveMapping(index)}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ===================== FOOTER ===================== */}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={createUser.loading}
              >
                {createUser.loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creating...
                  </>
                ) : (
                  <>Create User</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AddUsers; 